import { useEffect, useState, useMemo } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Lobbies from './components/Lobbies/index'
import Menu from './components/Menu'
import Sidebar from './components/Sidebar'
import MobileSidebar from './components/MobileSidebar'
import Messages from './components/Messages'
import useAuth from './hooks/useAuth'
import usePermissions from './hooks/usePermissions'
import useIsMobile from './hooks/useIsMobile'
import Liquidity from './components/Liquidity'
import UserSearch from './components/UserSearch'
import { Drawer, ThemeProvider } from '@mui/material'
import DriverPayments from './components/DriverPayments'
import PayoutReview from './components/PayoutReview'
import ChatBroadcast from './components/ChatBroadcast'
import NewsPopup from './components/NewsPopup'
import LoadingIndicator from './common/LoadingIndicator'
import Coupons from './components/Coupons'
import ImportCohort from './components/ImportCohort'
import Influencers from './components/Influencers'
import CRMUsers from './components/CRMUsers'
import Pricing from './components/Pricing'
import Discounts from './components/Discounts'
import LiveDashboard from './components/LiveDashboard'
import { getMuiTheme } from './utils'
import { authenticate } from './auth'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { getRouteTitle, getLegacyRedirectPath } from './app/routes'
import { NAVIGATION_SECTIONS } from './app/navigationConfig'
import useTheme from './hooks/useTheme'
import ThemeToggle from './common/ThemeToggle'

const getFirstAllowedPath = (permissions) => {
  for (const section of NAVIGATION_SECTIONS) {
    for (const item of section.items) {
      if (!item.requiredPermission || permissions.includes(item.requiredPermission)) {
        return item.path
      }
    }
  }
  return null
}

const ProtectedRoute = ({ permission, children }) => {
  const { hasPermission, permissions, permissionsLoading } = usePermissions()
  if (permissionsLoading) return null
  if (!hasPermission(permission)) {
    const fallback = getFirstAllowedPath(permissions)
    if (!fallback) return <div className='h-full flex items-center justify-center text-gray-500'>No tenés permisos asignados</div>
    return <Navigate to={fallback} replace />
  }
  return children
}

function LegacyRedirect() {
  const { permissions } = usePermissions()
  const redirectPath = getLegacyRedirectPath()
  if (redirectPath) {
    return <Navigate to={redirectPath} replace />
  }
  const fallback = getFirstAllowedPath(permissions) || '/lobbies'
  return <Navigate to={fallback} replace />
}

const App = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth()
  const { hasPermission, permissions } = usePermissions()
  const { theme } = useTheme()
  const muiTheme = useMemo(() => getMuiTheme(theme), [theme])
  const [selectedLobby, setSelectedLobby] = useState({})
  const [messages, setMessages] = useState(false)
  const [openDrawer, setOpenDrawer] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [openLobbySearch, setOpenLobbySearch] = useState(false)

  const location = useLocation()
  const isMobile = useIsMobile()
  const isLobbyPage = location.pathname === '/lobbies'
  const pageTitle = getRouteTitle(location.pathname)
  const params = new URLSearchParams(location.search)

  useEffect(() => {
    if (window.location.pathname === '/auth') {
      authenticate()
    }
  }, [])

  if (window.location.pathname === '/auth') {
    return (
      <div className='w-full h-screen flex justify-center items-center'>Inciando sesión...</div>
    )
  }

  if (isLoading) {
    return (
      <div className='h-screen w-screen flex items-center justify-center'>
        <LoadingIndicator size={26} />
      </div>
    )
  } else if (!isLoading && !isAuthenticated) {
    loginWithRedirect({
      appState: window.location.href
    })
    return (
      <div className='w-full h-screen flex justify-center items-center'>Inciando sesión...</div>
    )
  }

  return (
    <ThemeProvider theme={muiTheme}>
      {isMobile ? (
        <>
          <Drawer
            anchor='right'
            open={openDrawer}
            onClose={() => setOpenDrawer(false)}
            PaperProps={{
              sx: {
                background: theme === 'dark' ? '#171a1f' : 'white',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.45)',
                border: 'none',
                borderRadius: '24px 0 0 24px',
                width: '280px'
              }
            }}
          >
            <MobileSidebar
              setMessages={setMessages}
              onClose={() => setOpenDrawer(false)}
            />
          </Drawer>
          <div className='h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-dk-bg'>
            <Menu isMobile setMessages={setMessages}>
              <div className='flex items-center gap-2 flex-1'>
                <button
                  onClick={() => setOpenDrawer(true)}
                  data-collapse-toggle='navbar-default'
                  type='button'
                  className='inline-flex items-center p-3 text-gray-600 dark:text-gray-300 rounded-xl md:hidden focus:outline-none hover:bg-gray-100 dark:hover:bg-dk-hover transition-all duration-300 hover:shadow-md'
                  aria-controls='navbar-default'
                  aria-expanded='false'
                >
                  <svg
                    className='w-6 h-6'
                    aria-hidden='true'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      fillRule='evenodd'
                      d='M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z'
                      clipRule='evenodd'
                    ></path>
                  </svg>
                </button>
                <div className='ml-auto flex items-center gap-2'>
                  {isLobbyPage && (
                    <button
                      className='bg-white dark:bg-dk-raised hover:bg-slate-50 dark:hover:bg-dk-hover text-primary border-2 border-primary/20 hover:border-primary rounded-xl shadow-sm transition-all duration-200 w-10 h-10 flex items-center justify-center'
                      onClick={() => setOpenLobbySearch(true)}
                      title='Búsqueda por ID'
                    >
                      <FontAwesomeIcon icon={faSearch} className='text-base' />
                    </button>
                  )}
                  <ThemeToggle />
                </div>
              </div>
            </Menu>
            {/* Mobile Content */}
            <div className='flex-1 overflow-hidden min-h-0'>
              {hasPermission('read-lobby') && (
                <div className={`h-full ${!isLobbyPage ? 'hidden' : ''}`}>
                  <Lobbies
                    selectedLobby={selectedLobby}
                    setSelectedLobby={setSelectedLobby}
                    openSearchModal={openLobbySearch}
                    setOpenSearchModal={setOpenLobbySearch}
                  />
                </div>
              )}
              <Routes>
                <Route path='/liquidity' element={<ProtectedRoute permission='statistics'><Liquidity /></ProtectedRoute>} />
                <Route path='/user-search' element={<ProtectedRoute permission='read-user'><UserSearch defaultId={params.get('user_id')} /></ProtectedRoute>} />
                <Route path='/driver-payments' element={<ProtectedRoute permission='driver-payments'><DriverPayments /></ProtectedRoute>} />
                <Route path='/payout-review' element={<ProtectedRoute permission='driver-payments'><PayoutReview /></ProtectedRoute>} />
                <Route path='/chat-broadcast' element={<ProtectedRoute permission='broadcast'><ChatBroadcast /></ProtectedRoute>} />
                <Route path='/news-popup' element={<ProtectedRoute permission='broadcast'><NewsPopup /></ProtectedRoute>} />
                <Route path='/coupons' element={<ProtectedRoute permission='coupons'><Coupons /></ProtectedRoute>} />
                <Route path='/import-cohort' element={<ProtectedRoute permission='broadcast'><ImportCohort /></ProtectedRoute>} />
                <Route path='/crm-users' element={<ProtectedRoute permission='read-crm'><CRMUsers /></ProtectedRoute>} />
                <Route path='/influencers' element={<ProtectedRoute permission='influencers'><Influencers /></ProtectedRoute>} />
                <Route path='/pricing' element={<ProtectedRoute permission='read-pricing'><Pricing /></ProtectedRoute>} />
                <Route path='/discounts' element={<ProtectedRoute permission='discounts'><Discounts /></ProtectedRoute>} />
                <Route path='/live' element={<ProtectedRoute permission='read-lobby'><LiveDashboard /></ProtectedRoute>} />
                <Route path='/lobbies' element={
                  hasPermission('read-lobby') ? null : <Navigate to={getFirstAllowedPath(permissions) || '/'} replace />
                } />
                <Route path='*' element={<LegacyRedirect />} />
              </Routes>
            </div>
          </div>
        </>
      ) : (
        <div className='flex h-screen bg-gray-50 dark:bg-dk-bg'>
          <Sidebar
            setMessages={setMessages}
            collapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
          />
          <div className='flex-1 min-w-0'>
            {/* Top Header with Toggle Button */}
            <div className='bg-white dark:bg-dk-surface shadow-sm dark:shadow-black/30 px-6 flex items-center h-[4rem]'>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className='p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dk-hover transition-all duration-300 hover:shadow-md hover:scale-105'
              >
                <svg
                  className='w-6 h-6 text-gray-600 dark:text-gray-300'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M4 6h16M4 12h16M4 18h16'
                  />
                </svg>
              </button>
              <h1 className='ml-4 text-xl font-bold text-gray-800 dark:text-gray-100 tracking-tight flex-1'>
                {pageTitle}
              </h1>
              <div className='flex items-center gap-2'>
                {isLobbyPage && (
                  <button
                    className='bg-white dark:bg-dk-raised hover:bg-slate-50 dark:hover:bg-dk-hover text-primary border-2 border-primary/20 hover:border-primary rounded-xl shadow-sm transition-all duration-200 w-10 h-10 flex items-center justify-center'
                    onClick={() => setOpenLobbySearch(true)}
                    title='Búsqueda por ID'
                  >
                    <FontAwesomeIcon icon={faSearch} className='text-base' />
                  </button>
                )}
                <ThemeToggle />
              </div>
            </div>
            <div className='h-[calc(100vh-4rem)] bg-gray-50 dark:bg-dk-bg rounded-2xl overflow-hidden'>
              {hasPermission('read-lobby') && (
                <div className={`h-full ${!isLobbyPage ? 'hidden' : ''}`}>
                  <Lobbies
                    selectedLobby={selectedLobby}
                    setSelectedLobby={setSelectedLobby}
                    openSearchModal={openLobbySearch}
                    setOpenSearchModal={setOpenLobbySearch}
                  />
                </div>
              )}
              <Routes>
                <Route path='/liquidity' element={<ProtectedRoute permission='statistics'><Liquidity /></ProtectedRoute>} />
                <Route path='/user-search' element={<ProtectedRoute permission='read-user'><UserSearch defaultId={params.get('user_id')} /></ProtectedRoute>} />
                <Route path='/driver-payments' element={<ProtectedRoute permission='driver-payments'><DriverPayments /></ProtectedRoute>} />
                <Route path='/payout-review' element={<ProtectedRoute permission='driver-payments'><PayoutReview /></ProtectedRoute>} />
                <Route path='/chat-broadcast' element={<ProtectedRoute permission='broadcast'><ChatBroadcast /></ProtectedRoute>} />
                <Route path='/news-popup' element={<ProtectedRoute permission='broadcast'><NewsPopup /></ProtectedRoute>} />
                <Route path='/coupons' element={<ProtectedRoute permission='coupons'><Coupons /></ProtectedRoute>} />
                <Route path='/import-cohort' element={<ProtectedRoute permission='broadcast'><ImportCohort /></ProtectedRoute>} />
                <Route path='/crm-users' element={<ProtectedRoute permission='read-crm'><CRMUsers /></ProtectedRoute>} />
                <Route path='/influencers' element={<ProtectedRoute permission='influencers'><Influencers /></ProtectedRoute>} />
                <Route path='/pricing' element={<ProtectedRoute permission='read-pricing'><Pricing /></ProtectedRoute>} />
                <Route path='/discounts' element={<ProtectedRoute permission='discounts'><Discounts /></ProtectedRoute>} />
                <Route path='/live' element={<ProtectedRoute permission='read-lobby'><LiveDashboard /></ProtectedRoute>} />
                <Route path='/lobbies' element={
                  hasPermission('read-lobby') ? null : <Navigate to={getFirstAllowedPath(permissions) || '/'} replace />
                } />
                <Route path='*' element={<LegacyRedirect />} />
              </Routes>
            </div>
          </div>
        </div>
      )}
      {messages && <Messages visible={messages} setVisible={setMessages} />}
    </ThemeProvider>
  )
}

export default App

